import { SampleStatusEnum, isLockedSampleStatus } from 'src/app/enums/sampleStatus.enum';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { CalDriftBreach } from 'src/app/models/calDriftBreach.model';
import { Sample } from 'src/app/models/sample.model';
import { SampleValidationConfig, getSampleTypeRule } from 'src/app/models/sampleValidationConfig.model';

export function calVariabilityPercent(preCal: number | null | undefined, postCal: number | null | undefined): number | null {
    if (!preCal || !postCal) {
        return null;
    }
    return Math.abs((postCal - preCal) / preCal) * 100;
}

/** Configured max cal drift (%) for the sample type; null when no rule is set. Never falls back to the platform default. */
export function maxCalDriftPercentFor(config: SampleValidationConfig | null | undefined, sampleType: SampleTypeEnum | null | undefined): number | null {
    const rule = getSampleTypeRule(config, sampleType);
    return rule?.maxCalDriftPercent != null ? rule.maxCalDriftPercent * 100 : null;
}

export interface CalDriftWarningCheck {
    currentStatus: SampleStatusEnum;
    targetStatus: SampleStatusEnum;
    preCal: number | null | undefined;
    postCal: number | null | undefined;
    maxCalDriftPercent: number | null;
}

/**
 * True when the Calibration Variability Exceeded warning must be shown before a sample locks.
 *
 * The check must run *before* the status is persisted: the API rejects a locked -> unlocked
 * transition, so once a locked status is saved it cannot be undone and a cancelled void flow
 * would strand the sample (DASH-3182).
 *
 * A locked -> locked move never warns; the exceedance was acknowledged when the sample first
 * locked. Void keeps its own manual blank-reason flow. `>=` byte-matches the BE
 * PrePostCalVariabilityFlag check so the warning and the rendered flag never disagree at the
 * exact-equality boundary. Units cancel in the ratio, so the caller may pass either L/min or
 * mL/min as long as both values share one unit.
 */
export function shouldWarnCalDrift(check: CalDriftWarningCheck): boolean {
    const { currentStatus, targetStatus, preCal, postCal, maxCalDriftPercent } = check;
    if (!isLockedSampleStatus(targetStatus)
        || targetStatus === SampleStatusEnum.Void
        || targetStatus === SampleStatusEnum.VoidLetterSent
        || isLockedSampleStatus(currentStatus)) {
        return false;
    }
    const variability = calVariabilityPercent(preCal, postCal);
    return maxCalDriftPercent != null && variability != null && variability >= maxCalDriftPercent;
}

/** The samples a bulk move to `targetStatus` would lock with a cal variability at or over their type's configured max cal drift. */
export function findCalDriftBreaches(
    samples: Sample[],
    targetStatus: SampleStatusEnum,
    config: SampleValidationConfig | null | undefined
): CalDriftBreach[] {
    return samples.flatMap(sample => {
        const maxCalDriftPercent = maxCalDriftPercentFor(config, sample.sampleType);
        const variability = calVariabilityPercent(sample.samplePreCal, sample.samplePostCal);
        const warn = shouldWarnCalDrift({
            currentStatus: sample.sampleStatus,
            targetStatus,
            preCal: sample.samplePreCal,
            postCal: sample.samplePostCal,
            maxCalDriftPercent
        });
        if (!warn || variability == null || maxCalDriftPercent == null) {
            return [];
        }
        return [{
            sampleId: sample.id,
            sampleLabel: sample.sampleNumber || `Sample #${sample.id}`,
            calVariabilityPercent: variability,
            maxCalDriftPercent
        }];
    });
}
